import { currentLocale, translate } from '@/i18n'
import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'

import type { AuthResultDto, ProblemDetails } from '@/types/api'

/* ---------------------------------------------------------------- typed error */

export type ApiErrorKind =
  | 'VALIDATION'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'SERVER'
  | 'NETWORK'
  | 'TIMEOUT'
  | 'CANCELLED'
  | 'UNKNOWN'

/**
 * The single error type every caller in the app sees. Every axios failure — RFC7807
 * problem document, HTML error page, network drop, timeout, cancellation — is normalised
 * into one of these by {@link normaliseError}.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  /** Stable machine-readable `code` from the problem document, when present. */
  readonly code: string | null
  readonly title: string | null
  readonly detail: string | null
  readonly traceId: string | null
  /** Field-keyed validation messages, e.g. `{ "events[3].eventType": ["..."] }`. */
  readonly fieldErrors: Record<string, string[]>
  readonly problem: ProblemDetails | null

  constructor(init: {
    message: string
    kind: ApiErrorKind
    status?: number | null
    code?: string | null
    title?: string | null
    detail?: string | null
    traceId?: string | null
    fieldErrors?: Record<string, string[]>
    problem?: ProblemDetails | null
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.kind = init.kind
    this.status = init.status ?? null
    this.code = init.code ?? null
    this.title = init.title ?? null
    this.detail = init.detail ?? null
    this.traceId = init.traceId ?? null
    this.fieldErrors = init.fieldErrors ?? {}
    this.problem = init.problem ?? null
  }

  /** Flattened `field -> first message` map, for binding straight onto form inputs. */
  get firstFieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [field, messages] of Object.entries(this.fieldErrors)) {
      const first = messages[0]
      if (first) out[field] = first
    }
    return out
  }
}

const KIND_BY_STATUS: Record<number, ApiErrorKind> = {
  400: 'VALIDATION',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  422: 'VALIDATION',
  429: 'RATE_LIMITED',
}

function kindForStatus(status: number): ApiErrorKind {
  const mapped = KIND_BY_STATUS[status]
  if (mapped) return mapped
  if (status >= 500) return 'SERVER'
  if (status >= 400) return 'UNKNOWN'
  return 'UNKNOWN'
}

/**
 * Fallback wording per kind, resolved through i18n at the moment the error is built so a
 * toast raised on an Arabic screen reads Arabic (`apierrors.generic.*`).
 */
function genericMessage(kind: ApiErrorKind): string {
  return translate(`apierrors.generic.${kind}`)
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.title === 'string' ||
    typeof candidate.detail === 'string' ||
    typeof candidate.code === 'string' ||
    typeof candidate.type === 'string'
  )
}

function coerceFieldErrors(value: unknown): Record<string, string[]> {
  if (typeof value !== 'object' || value === null) return {}
  const out: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      out[field] = messages.filter((m): m is string => typeof m === 'string')
    } else if (typeof messages === 'string') {
      out[field] = [messages]
    }
  }
  return out
}

/**
 * Normalises anything axios (or the surrounding code) can throw into an {@link ApiError}.
 * Exported directly so it can be unit-tested without a live axios instance.
 */
export function normaliseError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isCancel(error)) {
    return new ApiError({ message: genericMessage('CANCELLED'), kind: 'CANCELLED' })
  }

  if (error instanceof AxiosError || (typeof error === 'object' && error !== null && 'isAxiosError' in error)) {
    const axiosError = error as AxiosError<unknown>

    if (!axiosError.response) {
      const timedOut =
        axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT'
      const kind: ApiErrorKind = timedOut ? 'TIMEOUT' : 'NETWORK'
      return new ApiError({
        message: genericMessage(kind),
        kind,
        code: axiosError.code ?? null,
      })
    }

    const status = axiosError.response.status
    const kind = kindForStatus(status)
    const body = axiosError.response.data

    if (isProblemDetails(body)) {
      const problem = body
      const message = problem.detail || problem.title || genericMessage(kind)
      return new ApiError({
        message,
        kind,
        status,
        code: problem.code ?? null,
        title: problem.title ?? null,
        detail: problem.detail ?? null,
        traceId: problem.traceId ?? null,
        fieldErrors: coerceFieldErrors(problem.errors),
        problem,
      })
    }

    // Non-JSON body (proxy HTML page, empty 502, a streamed blob, …).
    return new ApiError({ message: genericMessage(kind), kind, status })
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message || genericMessage('UNKNOWN'), kind: 'UNKNOWN' })
  }

  return new ApiError({ message: genericMessage('UNKNOWN'), kind: 'UNKNOWN' })
}

/* ------------------------------------------------------------- token plumbing */

/**
 * The auth store owns the tokens; the client only knows how to ask for them. This
 * indirection keeps `client.ts` free of a Pinia import (and therefore trivially
 * testable) and avoids an import cycle with the store.
 */
export interface TokenBridge {
  getAccessToken(): string | null
  getRefreshToken(): string | null
  /** Called after a successful silent refresh. */
  onRefreshed(result: AuthResultDto): void
  /** Called when the session cannot be recovered. */
  onSessionLost(): void
  /**
   * The session as every tab sees it right now (localStorage), or null. Optional so a
   * test bridge without storage still works.
   */
  getSharedSession?(): AuthResultDto | null
}

let tokenBridge: TokenBridge | null = null

export function registerTokenBridge(bridge: TokenBridge): void {
  tokenBridge = bridge
}

/** Test seam. */
export function clearTokenBridge(): void {
  tokenBridge = null
}

/* --------------------------------------------------------------------- client */

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api/v1'

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Guarantees the refresh-once contract: a request is only ever replayed one time. */
    _uoRetried?: boolean
    /** Opt out of the refresh dance (used by the auth endpoints themselves). */
    _uoSkipAuthRefresh?: boolean
  }
}

type RetryableConfig = InternalAxiosRequestConfig

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
})

http.interceptors.request.use((config) => {
  const token = tokenBridge?.getAccessToken() ?? null
  const headers = AxiosHeaders.from(config.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  // The API answers labels, captions and caveats in the language of the screen.
  headers.set('Accept-Language', currentLocale())
  config.headers = headers
  return config
})

/**
 * A single in-flight refresh is shared by every request that 401s at the same time, so a
 * burst of parallel calls produces one refresh, not N. Refresh tokens rotate server-side
 * (`docs/api.md` § Auth), so concurrent refreshes would invalidate each other.
 *
 * That is also true ACROSS TABS, and it was the cause of "The wall is not refreshing:
 * Your session is not valid". Every tab kept its own copy of the refresh token in memory.
 * When one tab refreshed, the server retired the old token and the new pair went to
 * localStorage -- but the Live Operations tab, polling every five seconds, still held the
 * retired one, presented it at its next 401, was refused, and logged everybody out.
 *
 * So the refresh now runs under a browser-wide lock (Web Locks API), and inside it the
 * shared session is read first: if another tab already refreshed while this one waited
 * (or slept in the background), its tokens are adopted without calling the server at all.
 * The new pair is written to storage INSIDE the lock, so the next tab in line sees it.
 */
let inFlightRefresh: Promise<AuthResultDto> | null = null

const REFRESH_LOCK = 'uobserve.auth.refresh'

function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks && typeof locks.request === 'function') {
    return locks.request(REFRESH_LOCK, fn) as Promise<T>
  }
  return fn()
}

async function refreshOnce(staleAccessToken: string | null): Promise<AuthResultDto> {
  if (inFlightRefresh) return inFlightRefresh

  inFlightRefresh = withCrossTabLock(async () => {
    const shared = tokenBridge?.getSharedSession?.() ?? null
    if (shared && shared.accessToken && shared.accessToken !== staleAccessToken) {
      // Another tab refreshed already. Adopt its pair; spending our stale token would
      // only get a refusal and end the session for every tab.
      tokenBridge?.onRefreshed(shared)
      return shared
    }

    const refreshToken = shared?.refreshToken ?? tokenBridge?.getRefreshToken() ?? null
    if (!refreshToken) {
      throw new ApiError({ message: genericMessage('UNAUTHORIZED'), kind: 'UNAUTHORIZED', status: 401 })
    }

    const response = await axios.post<AuthResultDto>(
      '/auth/refresh',
      { refreshToken },
      { baseURL: API_BASE_URL, headers: { Accept: 'application/json' } },
    )
    tokenBridge?.onRefreshed(response.data) // inside the lock: the next tab reads it
    return response.data
  }).finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

function bearerOf(config: RetryableConfig): string | null {
  const value = AxiosHeaders.from(config.headers).get('Authorization')
  return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice(7) : null
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const axiosError = error as AxiosError<unknown>
    const config = axiosError.config as RetryableConfig | undefined
    const status = axiosError.response?.status

    const canRetry =
      status === 401 && config !== undefined && !config._uoRetried && !config._uoSkipAuthRefresh

    if (!canRetry) {
      throw normaliseError(error)
    }

    config._uoRetried = true

    try {
      const refreshed = await refreshOnce(bearerOf(config))
      const headers = AxiosHeaders.from(config.headers)
      headers.set('Authorization', `Bearer ${refreshed.accessToken}`)
      config.headers = headers
      return await http.request(config)
    } catch {
      // Refresh failed (or the replay 401'd again): the session is unrecoverable.
      tokenBridge?.onSessionLost()
      throw normaliseError(error)
    }
  },
)

/* ------------------------------------------------------------------- helpers */

/** Strips `undefined`, `null` and empty strings so we never send `?siteId=`. */
export function toQueryParams(source: Record<string, unknown>): Record<string, string> {
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(source)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && value.trim() === '') continue
    params[key] = typeof value === 'string' ? value : String(value)
  }
  return params
}

export async function getJson<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response = await http.get<T>(url, config)
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

export async function postJson<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    const response = await http.post<T>(url, body, config)
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

export async function putJson<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    const response = await http.put<T>(url, body, config)
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

export async function deleteJson<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
  try {
    const response = await http.delete<T>(url, config)
    return response.data
  } catch (error) {
    throw normaliseError(error)
  }
}

/**
 * Fetches a protected binary resource with the bearer header and returns an object URL.
 * Used for snapshots, thumbnails and clips — a token must never appear in an `src`
 * attribute, where it would leak into history, referrers and screenshots.
 *
 * The caller owns the returned URL and must `URL.revokeObjectURL` it.
 */
export async function getBlobUrl(url: string, config?: AxiosRequestConfig): Promise<string> {
  try {
    const response = await http.get<Blob>(url, { ...config, responseType: 'blob' })
    return URL.createObjectURL(response.data)
  } catch (error) {
    throw normaliseError(error)
  }
}
